import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Target, Users, Award, Zap, Globe, Shield, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function About() {
  const stats = [
    { value: '10M+', label: 'QR Codes Created' },
    { value: '50K+', label: 'Happy Customers' },
    { value: '150+', label: 'Countries' },
    { value: '99.9%', label: 'Uptime' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Mission-Driven',
      description: 'Empowering businesses to bridge physical and digital worlds seamlessly through innovative QR technology.',
    },
    {
      icon: Users,
      title: 'Customer-First',
      description: 'Your success is our priority. We build features based on real user feedback and needs.',
    },
    {
      icon: Award,
      title: 'Quality Excellence',
      description: 'Enterprise-grade reliability with 99.9% uptime and bank-level security for your data.',
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'Constantly pushing boundaries with AI design, blockchain authentication, and AR experiences.',
    },
  ];

  const features = [
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Trusted by businesses in 150+ countries, from startups to Fortune 500 companies.',
    },
    {
      icon: Shield,
      title: 'Security First',
      description: 'GDPR compliant, SSL encrypted, and SOC 2 Type II certified infrastructure.',
    },
    {
      icon: Sparkles,
      title: 'AI-Powered',
      description: 'Industry-first AI design suggestions and automatic optimization for better scan rates.',
    },
    {
      icon: TrendingUp,
      title: 'Data-Driven',
      description: 'Real-time analytics and insights to measure ROI and optimize your campaigns.',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative py-20 px-4 overflow-hidden"
        >
          {/* Animated background */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse" />
          </div>

          <div className="relative max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                About{' '}
                <span className="gradient-text">
                  NexQR
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
                We're on a mission to revolutionize how businesses connect with their customers through intelligent, beautiful, and trackable QR codes.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="px-4 py-12"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-4xl md:text-5xl font-black tracking-tight gradient-text mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Story Section */}
        <section className="px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="prose prose-lg dark:prose-invert max-w-none"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Our Story</h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                NexQR was born from a simple frustration: existing QR code generators were either too basic or too complicated. We believed businesses deserved a tool that was powerful yet intuitive, affordable yet enterprise-grade.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                In 2024, our team of designers, engineers, and marketers came together with a vision: to create the world's most advanced QR code platform. We didn't just build a QR generator—we built a complete marketing ecosystem with AI-powered design, real-time analytics, A/B testing, and cutting-edge features like blockchain authentication and AR experiences.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Today, NexQR powers millions of QR codes across 150+ countries, helping businesses of all sizes connect with their customers in meaningful ways. From local restaurants to global brands, we're proud to be the platform they trust.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Values Section */}
        <section className="px-4 py-20 bg-muted/20">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-12 text-center"
            >
              Our Core Values
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card border border-border rounded-2xl p-8 hover:shadow-xl transition-shadow"
                >
                  <div className="w-14 h-14 rounded-xl gradient-hero flex items-center justify-center mb-6">
                    <value.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="px-4 py-20">
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-12 text-center"
            >
              Why Choose NexQR?
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="px-4 py-20"
        >
          <div className="max-w-4xl mx-auto text-center gradient-hero rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Join 50,000+ Happy Customers
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Start creating stunning QR codes today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild className="text-lg px-8">
                <Link to="/create">Create Free QR Code</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-lg px-8 bg-white/10 text-white border-white/20 hover:bg-white/20">
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </div>
        </motion.section>
      </div>
    </Layout>
  );
}
